<template>
    <div class="container-fluid px-4">

        <BaseTable :rows="workgroups" :columns="columns" :loading="loading" searchable :selectable="false"
            :pagination="pagination" @changePage="onChangePage" @updatePageSize="onUpdatePageSize"
            @searchChanged="onSearchChanged">

            <template #buttons>
                <material-button v-if="!readOnly" @click="showAddWorkgroup = true" variant="secondary" color="info" class="px-4"
                    :disabled="isSaving">
                    <i class="material-icons text-lg me-1">add</i>
                    Add New Workgroup
                </material-button>
            </template>

            <template #col-status="{ row }">
                <MaterialSelectedBadge :label="row.status" :color="row.status?.toLowerCase() === 'inactive' ? 'danger' : 'info'" />
            </template>

            <template #col-setupStatus="{ row }">
                <button class="btn btn-link p-0 m-0" @click="openSetupStatus(row)" style="text-decoration: none;">
                    <MaterialSelectedBadge :label="row.setupStatus" :color="row.setupStatus == 'Complete' ? 'success' : 'warning'" />
                </button>
            </template>

            <template #col-wgSummary="{ row }">
                <button class="btn btn-sm btn-outline-success my-0" @click="openWgSummary(row)">
                    <i class="material-icons text-sm align-middle">summarize</i>
                </button>
            </template>

            <template #actions="{ row }">
                <button v-if="canEditWorkgroup(row.id)" class="btn btn-sm my-0 py-0" @click="editWorkgroup(row)">
                    <i class="material-icons text-lg text-orange">edit</i>
                </button>
                <button v-else class="btn btn-sm my-0 py-0" disabled title="View only">
                    <i class="material-icons text-lg" style="color: #6c757d;">visibility</i>
                </button>
                <button v-if="canEditWorkgroup(row.id)" class="btn btn-sm my-0 py-0" @click="openDeleteModal(row)">
                    <i class="material-icons text-lg text-danger">delete</i>
                </button>
            </template>

        </BaseTable>

        <ContentModal v-model="showAddWorkgroup" title="Add New Workgroup" :showClose="false">
            <div class="d-flex justify-content-end">
                <MaterialButton variant="text" color="info" @click="viewPersons">
                    View Persons
                </MaterialButton>
            </div>
            <div class="card-body">
                <form @submit.prevent="saveSkill">
                    <div class="row mb-3">
                        <div class="col-md-6">
                            <MaterialSelect label="Skill" v-model="addWorkgroupData.workgroupskillid"
                                :options="skillOptions" />
                        </div>
                        <div class="col-md-6">
                            <MaterialInput id="add-workgroup-name" label="Workgroup Name" v-model="addWorkgroupData.workgroupname"
                                placeholder="Enter workgroup name" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-12">
                            <MaterialInput id="add-workgroup-description" type="textarea" label="Description" v-model="addWorkgroupData.description"
                                placeholder="Enter description" rows="3" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-6">
                            <MaterialInput id="add-workgroup-person-count" type="number" label="Person Count" v-model="addWorkgroupData.personcount"
                                min="0" />
                        </div>
                        <div class="col-md-6">
                            <MaterialSelect label="Enable Auto-Scheduling"
                                v-model="addWorkgroupData.addworkgroupenableautoschedule" :options="yesNoOptions" />
                        </div>
                    </div>

                    <div class="row mb-3">
                        <div class="col-md-6">
                            <MaterialSelect label="Add to Upcoming Existing Instances/Dates"
                                v-model="addWorkgroupData.addworkgrouptoinstance" :options="yesNoOptions" />
                        </div>
                        <div class="col-md-6">
                            <MaterialSelect label="Send SMS/Text Messaging" v-model="addWorkgroupData.sendwgsms"
                                :options="yesNoOptions" />
                        </div>
                    </div>

                    <div class="row mb-4">
                        <div class="col-md-12">
                            <MaterialInput id="add-workgroup-admin-email" type="email" label="Admin Reply-to Email"
                                v-model="addWorkgroupData.adminemail" placeholder="email@location.com" 
                                :error="!!adminEmailError" @blur="validateAdminEmail" />
                            <small v-if="adminEmailError" class="text-danger">{{ adminEmailError }}</small>
                        </div>
                    </div>
                </form>
            </div>
            <template #footer>

                <div class="d-flex justify-content-end gap-2">
                    <MaterialButton @click="saveSkill" variant="primary" class="px-4" :disabled="isSaving">
                        <span v-if="isSaving" class="spinner-border spinner-border-sm me-2" role="status"
                            aria-hidden="true"></span>
                        <i v-else class="material-icons text-lg me-1">save</i>
                        {{ isSaving ? 'Saving...' : 'Save' }}
                    </MaterialButton>
                    <MaterialButton variant="secondary" class="px-4" color="danger" type="button"
                        @click="showAddWorkgroup = false">
                        <i class="material-icons text-lg me-1">block</i>
                        Cancel
                    </MaterialButton>
                </div>

            </template>
        </ContentModal>

        <!-- Persons Modal -->
        <ContentModal v-model="showPersonsModal" title="Persons with Selected Skill" size="xl">
            <BaseTable :rows="personsWithSkill" :columns="skillPersonsColumns" :selectable="false" :searchable="false"
                :loading="personsLoading" :pagination="false" />
        </ContentModal>

        <DeleteModal v-model:show="showDeleteModal" :itemName="selectedWorkgroup?.workgroupName"
            :confirm-disabled="deletePreviewLoading || !!deletePreviewError" @confirm="deleteWorkgroup">
            <template #details>
                <div class="alert alert-warning py-2 mb-3" role="alert">
                    Deleting this Activity Workgroup will also delete its future Activity Instance Workgroups.
                    Past instance workgroups and their Activity Instances will be retained.
                </div>

                <div v-if="deletePreviewLoading" class="d-flex align-items-center gap-2 text-secondary" role="status">
                    <span class="spinner-border spinner-border-sm" aria-hidden="true"></span>
                    Loading future workgroup instances...
                </div>
                <div v-else-if="deletePreviewError" class="alert alert-danger py-2 mb-0" role="alert">
                    <div>{{ deletePreviewError }}</div>
                    <button type="button" class="btn btn-sm btn-outline-danger mt-2" @click="loadDeletePreview">
                        Retry
                    </button>
                </div>
                <div v-else-if="futureInstanceWorkgroups.length === 0" class="text-secondary">
                    No future Activity Instance Workgroups currently use this workgroup.
                </div>
                <div v-else class="table-responsive border rounded" style="max-height: 16rem; overflow-y: auto;">
                    <table class="table table-sm table-striped mb-0">
                        <thead class="table-light">
                            <tr>
                                <th scope="col">Instance Date</th>
                                <th scope="col">Workgroup</th>
                                <th scope="col" class="text-end">People Assigned</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="instance in futureInstanceWorkgroups" :key="instance.ai_workgroup_id">
                                <td>{{ instance.start_datetime_formatted || 'Date unavailable' }}</td>
                                <td>{{ instance.name }}</td>
                                <td class="text-end">{{ instance.persons_assigned ?? 0 }}</td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </template>
        </DeleteModal>

        <SetupStatusModal v-model="showSetupStatusModal" :setupStatus="selectedSetupStatus" entityLabel="workgroup" />
        <SummaryModal v-model="showWgSummaryModal" :title="wgSummaryTitle" :summaryList="wgSummaryData" />
    </div>
</template>

<script setup>
import { ref, onMounted, computed, reactive, watch } from 'vue'
import { useStore } from 'vuex'
import { useToast } from 'vue-toast-notification'
import { useRoute, useRouter } from 'vue-router'
import BaseTable from '@/components/Table/Table.vue'
import DeleteModal from '@/components/Modal/ConfirmationModal.vue'
import MaterialInput from '@/components/common/MaterialInput.vue'
import MaterialSelect from '@/components/common/MaterialSelect.vue'
import MaterialButton from '../../../components/common/MaterialButton.vue'
import MaterialSelectedBadge from "@/components/common/MaterialSelectedBadge.vue";
import ContentModal from '../../../components/Modal/ContentModal.vue'
import { isValidEmail } from '../../../utils/validation'
import { usePermissions } from '@/composables/usePermissions'
import { useBreadcrumb } from '@/composables/useBreadcrumb'
import SetupStatusModal from '@/components/Modal/SetupStatusModal.vue'
import SummaryModal from '@/components/Modal/SummaryModal.vue'

defineProps({
    readOnly: { type: Boolean, default: false }
})

const toast = useToast()
const store = useStore()
const route = useRoute()
const router = useRouter()
const { canEditWorkgroup } = usePermissions()
const { pushCrumb } = useBreadcrumb()

const searchTerm = ref('')
const showDeleteModal = ref(false)
const selectedWorkgroup = ref(null)
const futureInstanceWorkgroups = ref([])
const deletePreviewLoading = ref(false)
const deletePreviewError = ref('')
const showSetupStatusModal = ref(false)
const selectedSetupStatus = ref(null)

const openSetupStatus = (row) => {
    selectedSetupStatus.value = {
        setupcomplete: row.setupStatus === 'Complete',
        setupitemsarray: row.setupStatus === 'Complete' ? [] : ['This workgroup may need additional configuration'],
    }
    showSetupStatusModal.value = true
}

const showWgSummaryModal = ref(false)
const wgSummaryData = ref([])
const wgSummaryTitle = ref('')

const openWgSummary = async (row) => {
    wgSummaryTitle.value = `Summary: ${row.workgroupName}`
    try {
        wgSummaryData.value = await store.dispatch('fetchWorkgroupSummary', {
            activityId: activityId,
            workgroupId: row.id,
        })
        showWgSummaryModal.value = true
    } catch (e) {
        console.error('Error loading workgroup summary:', e)
    }
}
const loading = ref(true)
const editedRowIds = ref(new Set())
const showAddWorkgroup = ref(false);
const adminEmailError = ref(null);

const locationId = route.params.locationid
const activityId = route.params.activityid

const workgroups = computed(() => store.getters.allWorkgroups || [])
const pagination = computed(() => store.getters.workgroupPagination)
const skillOptions = computed(() => store.getters.skillsForWorkgroup)

const showPersonsModal = ref(false);
const personsWithSkill = computed(() => store.getters.skillPersons || [])
const personsLoading = ref(false);

const columns = [
    { key: 'id', label: 'ID' },
    { key: 'workgroupName', label: 'Workgroup Name' },
    { key: 'description', label: 'Description' },
    { key: 'status', label: 'Status' },
    { key: 'setupStatus', label: 'Setup Status' },
    { key: 'wgSummary', label: 'Summary' },
    { key: 'personCount', label: 'Person Count' },
    { key: 'skill', label: 'Skill' },
    { key: 'priority', label: 'Priority' },
    { key: 'sendSmsReminders', label: 'Send SMS Reminders' },
    { key: 'sendSecondSms', label: 'Send 2nd SMS' },
    { key: 'autoSchedule', label: 'Auto-Schedule' },
    { key: 'autoSubstitute', label: 'Auto-Substitute' },
    { key: 'swapEnabled', label: 'Swap Enabled' },
    { key: 'allowOverlap', label: 'Allow Overlap' },
    { key: 'signUpOnly', label: 'Sign-Up Only' },
    { key: 'convertToRequest', label: 'Convert-To-Request' },
    { key: 'autoDecline', label: 'Auto-Decline' },
    { key: 'publiclyViewable', label: 'Publicly Viewable' },
    { key: 'teamsEnabled', label: 'Teams Enabled' },
]

const skillPersonsColumns = [
    { key: "personid", label: "ID" },
    { key: "fullname", label: "Name" },
    { key: "email", label: "Email" },
    { key: "skilllevel", label: "Skill Level" },
];

const viewPersons = async () => {
    if (!addWorkgroupData.workgroupskillid) {
        toast.warning("Please select a skill first!", { position: "top-right" });
        return;
    }

    showPersonsModal.value = true;
    personsLoading.value = true;

    try {
        await store.dispatch("fetchSkillPersons", {
            skillid: addWorkgroupData.workgroupskillid,
            locationid: locationId,
        });
    } catch (error) {
        console.error("Error fetching skill persons:", error);
        toast.error("Failed to load persons for selected skill.", {
            position: "top-right",
        });
    } finally {
        personsLoading.value = false;
    }
};

const fetchWorkgroups = async (page = 1, pageSize = 10) => {
    loading.value = true
    try {
        await store.dispatch('fetchWorkgroups', {
            activityId,
            search: searchTerm.value || '',
            page,
            pageSize,
        })
    } catch (error) {
        console.error('Error fetching workgroups:', error)
        toast.error('Failed to load workgroups', { position: 'top-right' })
    } finally {
        loading.value = false
    }
}

const fetchSkills = async () => {
    try {
        await store.dispatch('fetchAllSkillsByLocation', locationId);
    } catch (error) {
        console.error('Error fetching skills:', error);
    }
};

const onChangePage = (page) => {
    if (!page) return
    fetchWorkgroups(page)
}

const onUpdatePageSize = (size) => {
    fetchWorkgroups(1, size)
}

const onSearchChanged = (term) => {
    searchTerm.value = term
    fetchWorkgroups(1)
}

const openDeleteModal = (workgroup) => {
    selectedWorkgroup.value = workgroup
    futureInstanceWorkgroups.value = []
    deletePreviewError.value = ''
    showDeleteModal.value = true
    loadDeletePreview()
}

const loadDeletePreview = async () => {
    if (!selectedWorkgroup.value) return

    deletePreviewLoading.value = true
    deletePreviewError.value = ''
    try {
        futureInstanceWorkgroups.value = await store.dispatch(
            'fetchFutureActivityWorkgroupInstances',
            selectedWorkgroup.value.id
        )
    } catch (error) {
        deletePreviewError.value = error?.message || error || 'Failed to load future workgroup instances'
    } finally {
        deletePreviewLoading.value = false
    }
}

const editWorkgroup = (row) => {
    pushCrumb({
        label: row.workgroupName || `Workgroup #${row.id}`,
        icon: 'group_work',
        route: { name: 'editWorkgroup', params: { locationId, workgroupid: row.id, skillid: row.skillId } },
    })
    router.push({
        name: 'editWorkgroup',
        params: { locationId, workgroupid: row.id, skillid: row.skillId },
    })
}

const deleteWorkgroup = async () => {
    if (!selectedWorkgroup.value) return
    try {
        await store.dispatch('deleteWorkgroup', {
            locationid: locationId,
            workgroupid: selectedWorkgroup.value.id,
        });
        toast.success('Workgroup deleted successfully!', { position: 'top-right' });
        editedRowIds.value.delete(selectedWorkgroup.value.id);
        fetchWorkgroups(pagination.value.currentPage || 1);
    } catch (error) {
        console.error('Error deleting workgroup:', error);
        toast.error(
            error || 'An error occurred while deleting the workgroup',
            { position: 'top-right' }
        );
    } finally {
        showDeleteModal.value = false;
        selectedWorkgroup.value = null;
    }
};

const isSaving = ref(false)

const yesNoOptions = [
    { label: 'Yes', value: 'true' },
    { label: 'No', value: 'false' },
]

const addWorkgroupData = reactive({
    activityid: route.params.activityid,
    workgroupskillid: '',
    workgroupname: '',
    description: '',
    personcount: 1,
    addworkgroupenableautoschedule: 'no',
    addworkgrouptoinstance: 'no',
    sendwgsms: 'no',
    adminemail: '',
})

const validateAdminEmail = () => {
    if (addWorkgroupData.adminemail && addWorkgroupData.adminemail.trim() !== '') {
        if (!isValidEmail(addWorkgroupData.adminemail)) {
            adminEmailError.value = 'Please enter a valid email address';
        } else {
            adminEmailError.value = null;
        }
    } else {
        adminEmailError.value = null;
    }
};

const resetAddWorkgroupData = () => {
    addWorkgroupData.workgroupskillid = ''
    addWorkgroupData.workgroupname = ''
    addWorkgroupData.description = ''
    addWorkgroupData.personcount = 1
    addWorkgroupData.addworkgroupenableautoschedule = 'no'
    addWorkgroupData.addworkgrouptoinstance = 'no'
    addWorkgroupData.sendwgsms = 'no'
    addWorkgroupData.adminemail = ''
    adminEmailError.value = null
}

const saveSkill = async () => {
    // Validate email before saving
    validateAdminEmail();
    if (adminEmailError.value) {
        toast.error("Please fix validation errors before saving", { position: 'top-right' });
        return;
    }
    
    isSaving.value = true
    try {
        var workgroupResponse = await store.dispatch("createWorkgroup", addWorkgroupData);

        if (workgroupResponse) {
            toast.success(workgroupResponse.message || 'Workgroup added successfully!', { position: 'top-right' });
            await fetchWorkgroups(pagination.value.currentPage || 1)
            showAddWorkgroup.value = false
            resetAddWorkgroupData()
        }

    } catch (error) {
        console.error('[Error Saving Skill Configuration]', error)
    } finally {
        isSaving.value = false
    }
}

onMounted(async () => {
    await fetchWorkgroups();
    await fetchSkills();
})

watch(
    () => addWorkgroupData.workgroupskillid,
    (newSkill) => {
        if (!newSkill) {
            addWorkgroupData.workgroupname = ''
            return
        }
        const skillOption = skillOptions.value.find(s => s.value === newSkill)
        addWorkgroupData.workgroupname = skillOption ? skillOption.label : ''
    }
)
</script>
